/**
 * QuickAddUsersProvider - mounts the Quick Add User modal once for the whole app.
 *
 * Atomic Design role: Organism (provider). Holds the open request, renders
 * QuickAddUsersModal for it, and publishes QuickAddUsersContext so any picker
 * — molecule or organism — can open the modal without owning it. Mounted in
 * App.tsx inside AuthProvider, because whether quick-add is offered at all
 * depends on the viewer's role (USER_CREATE_ROLES).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { USER_CREATE_ROLES } from '../../../utils/constants';
import { QuickAddUsersContext } from '../../../context/QuickAddUsersContext';
import type { QuickAddRequest, QuickAddUsersContextValue } from '../../../context/QuickAddUsersContext';
import { QuickAddUsersModal } from './QuickAddUsersModal';

export const QuickAddUsersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { hasAnyRole, isAuthenticated } = useAuth();
	/* The request the modal is currently serving; null = closed. */
	const [request, setRequest] = useState<QuickAddRequest | null>(null);

	const canQuickAdd = isAuthenticated && hasAnyRole([...USER_CREATE_ROLES]);

	const openQuickAdd = useCallback((next: QuickAddRequest) => {
		if (canQuickAdd) setRequest(next);
	}, [canQuickAdd]);

	const value = useMemo<QuickAddUsersContextValue>(() => ({ canQuickAdd, openQuickAdd }), [canQuickAdd, openQuickAdd]);
	const close = useCallback(() => setRequest(null), []);

	return (
		<QuickAddUsersContext.Provider value={value}>
			{children}
			{request && (
				<QuickAddUsersModal
					isOpen
					onClose={close}
					defaultCompanyId={request.defaultCompanyId}
					initialName={request.initialName}
					single={request.single}
					onCreated={request.onCreated}
				/>
			)}
		</QuickAddUsersContext.Provider>
	);
};

export default QuickAddUsersProvider;
